import {
  Button,
  Card,
  Empty,
  Flex,
  Segmented,
  Skeleton,
  Tag,
  Typography
} from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { empty } from "../../../constants/placeholders.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../../hooks/use-date-format.ts"
import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import * as services from "../api/services.ts"
import {
  COMPLIANCE_DOCUMENT_LABELS,
  COMPLIANCE_DOCUMENT_ORDER,
  COMPLIANCE_STATUS_COLOR_MAP
} from "../constants/compliance.ts"
import { SubcontractorFormModal } from "../components/SubcontractorForm/SubcontractorFormModal.tsx"
import { type SubcontractorFormData } from "../components/SubcontractorForm/subcontractor-form-data.ts"
import {
  subcontractorEditableStatuses,
  subcontractorStatusColorMap
} from "../constants/suctontractor-statuses.ts"
import { updateSubcontractor } from "../redux/subcontractors.thunks.ts"
import {
  type SubcontractorComplianceState,
  type SubcontractorListItem
} from "../types/types.ts"

const { Title, Text } = Typography

type ProjectsMapView = "active" | "past"

const SubcontractorDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const [subcontractor, setSubcontractor] =
    useState<SubcontractorListItem | null>(null)
  const [fetching, setFetching] = useState(true)
  // Only a 404 means the record is gone; a 500 or a dropped connection is a
  // failure to load, which the user can retry.
  const [loadError, setLoadError] = useState<"notFound" | "failed" | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const { t } = useTranslation()
  const { parseDate, userFormat } = useDateFormat()
  const [projectsMapView, setProjectsMapView] =
    useState<ProjectsMapView>("active")
  const [editing, setEditing] = useState(false)
  const dispatch = useAppDispatch()
  const permissions = useAppSelector(
    (state) => state?.auth?.permissions?.codes?.subcontractors
  )
  const editAllowed = permissions?.subcontractors?.subcontractor_edit

  useEffect(() => {
    generateDocumentTitle("Subcontractor")
  }, [])

  useEffect(() => {
    if (!id) return

    let cancelled = false
    // Never show the previous record while another one loads.
    setSubcontractor(null)
    setFetching(true)
    setLoadError(null)

    services.getSubcontractorDetails(id).then((response) => {
      if (cancelled) return

      if (!response.ok) {
        setLoadError(response.status === 404 ? "notFound" : "failed")
        setFetching(false)
        return
      }

      setSubcontractor(response.data as SubcontractorListItem)
      setFetching(false)
    })

    return () => {
      cancelled = true
    }
  }, [id, reloadKey])

  const handleSave = async ({
    send_invitation: _sendInvitation,
    ...data
  }: SubcontractorFormData) => {
    if (!id) return

    const result = await dispatch(updateSubcontractor({ ...data, id }))
    if (!updateSubcontractor.fulfilled.match(result)) return

    // The PATCH response omits the derived detail fields (compliance status,
    // counts, project types), so re-read the record rather than merging.
    const refreshed = await services.getSubcontractorDetails(id)
    if (refreshed.ok) setSubcontractor(refreshed.data as SubcontractorListItem)
    setEditing(false)
  }

  if (fetching && !subcontractor) {
    return (
      <Card size={"small"}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    )
  }

  if (loadError === "failed") {
    return (
      <Card size={"small"}>
        <Empty description={t("subcontractorDetail.loadError")}>
          <Button onClick={() => setReloadKey((n) => n + 1)}>
            {t("subcontractorDetail.retry")}
          </Button>
        </Empty>
      </Card>
    )
  }

  if (!subcontractor || loadError === "notFound") {
    return (
      <Card size={"small"}>
        <Empty description={t("subcontractorDetail.notFound")} />
      </Card>
    )
  }

  const normalizedStatus =
    subcontractor.status?.toLowerCase() as keyof typeof subcontractorStatusColorMap
  // Same rule the list applies to its "View Profile" (edit) action.
  const isEditable =
    !!editAllowed &&
    subcontractorEditableStatuses.includes(normalizedStatus as never)
  const complianceEntries = (
    Object.entries(subcontractor.compliance_status || {}) as Array<
      [
        string,
        { status: SubcontractorComplianceState; expiration_date: string | null }
      ]
    >
  ).sort(
    ([a], [b]) =>
      COMPLIANCE_DOCUMENT_ORDER.indexOf(a) -
      COMPLIANCE_DOCUMENT_ORDER.indexOf(b)
  )

  return (
    <Flex vertical gap={16}>
      <Card size={"small"}>
        <Flex justify={"space-between"} align={"center"} wrap gap={12}>
          <Flex vertical gap={4}>
            <Title level={4} className={"!mb-0"}>
              {getEntityName(subcontractor)}
            </Title>
            <Text type={"secondary"}>{subcontractor.email || empty}</Text>
          </Flex>
          <Flex align={"center"} gap={12}>
            <Tag
              color={subcontractorStatusColorMap[normalizedStatus] ?? "default"}
            >
              {subcontractor.status || empty}
            </Tag>
            {isEditable && (
              <Button type={"primary"} onClick={() => setEditing(true)}>
                {t("subcontractorDetail.edit")}
              </Button>
            )}
          </Flex>
        </Flex>
      </Card>

      {editing && (
        <SubcontractorFormModal
          onSave={handleSave}
          onCancel={() => setEditing(false)}
          // The API's nullable columns come back as null; the form models them as
          // absent, which antd treats identically.
          initialValues={subcontractor as Partial<SubcontractorFormData>}
        />
      )}

      <Flex gap={16} wrap>
        <Card
          size={"small"}
          title={t("subcontractorDetail.totalActiveProjects")}
          className={"flex-1 min-w-[200px]"}
        >
          <Text className={"text-2xl font-semibold"}>
            {subcontractor.active_assignments_count ?? 0}
          </Text>
        </Card>
        <Card
          size={"small"}
          title={t("subcontractorDetail.totalProjects")}
          className={"flex-1 min-w-[200px]"}
        >
          <Text className={"text-2xl font-semibold"}>
            {subcontractor.assignments_count ?? 0}
          </Text>
        </Card>
      </Flex>

      <Card size={"small"} title={t("subcontractorDetail.projectTypes")}>
        {subcontractor.project_types?.length ? (
          <Flex gap={8} wrap>
            {subcontractor.project_types.map((type) => (
              <Tag key={type}>{type}</Tag>
            ))}
          </Flex>
        ) : (
          <Text type={"secondary"}>{empty}</Text>
        )}
      </Card>

      <Card size={"small"} title={t("subcontractorDetail.expirationAlarms")}>
        {complianceEntries.length ? (
          <Flex vertical gap={8}>
            {complianceEntries.map(([docType, compliance]) => (
              <Flex key={docType} justify={"space-between"} align={"center"}>
                <Text>{COMPLIANCE_DOCUMENT_LABELS[docType] || docType}</Text>
                <Flex gap={8} align={"center"}>
                  <Text type={"secondary"}>
                    {parseDate(compliance.expiration_date)?.format(
                      userFormat || "YYYY-MM-DD"
                    ) ?? empty}
                  </Text>
                  <Tag
                    color={
                      COMPLIANCE_STATUS_COLOR_MAP[compliance.status] ?? "default"
                    }
                  >
                    {t(`subcontractorCompliance.status.${compliance.status}`, {
                      defaultValue: compliance.status
                    })}
                  </Tag>
                </Flex>
              </Flex>
            ))}
          </Flex>
        ) : (
          <Text type={"secondary"}>
            {t("subcontractorDetail.noExpiringDocuments")}
          </Text>
        )}
      </Card>

      <Card
        size={"small"}
        title={t("subcontractorDetail.projectsMap")}
        extra={
          <Segmented
            size={"small"}
            value={projectsMapView}
            onChange={(value) => setProjectsMapView(value as ProjectsMapView)}
            options={[
              { label: t("subcontractorDetail.active"), value: "active" },
              { label: t("subcontractorDetail.past"), value: "past" }
            ]}
          />
        }
      >
        <Flex
          align={"center"}
          justify={"center"}
          className={"h-64 rounded bg-gray-50"}
        >
          <Text type={"secondary"}>
            {projectsMapView === "active"
              ? t("subcontractorDetail.activeMapSoon")
              : t("subcontractorDetail.pastMapSoon")}
          </Text>
        </Flex>
      </Card>
    </Flex>
  )
}

export default SubcontractorDetailPage
